<template>
  <div class="kline-chart-wrapper">
    <div class="chart-container" ref="chartContainer">
      <div id="kline-chart" ref="klineChart">
        <!-- K线图动态生成区域 -->
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DynamicKlineChart',
  data() {
    return {
      // 核心配置（与原配置一致）
      CONFIG: {
        speed: 7,
        klineCount: 60,
        volatility: 3,
        basePrice: 100,
        gridLines: 0,
        pricePrecision: 2,
        updateInterval: 100
      },
      // 全局数据
      klines: [],
      animationFrameId: null,
      lastUpdateTime: 0,
      priceExtremesCache: { min: 100, max: 100, range: 1 },
      klineElements: [],
      // 缓存DOM引用
      chartContainer: null,
      klineChart: null
    };
  },
  mounted() {
    // 初始化DOM引用
    this.chartContainer = this.$refs.chartContainer;
    this.klineChart = this.$refs.klineChart;
    // 初始化K线
    this.initKlines();
    // 启动动画
    this.animationFrameId = requestAnimationFrame(this.animate);
    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    // 清理动画
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    // 移除事件监听
    window.removeEventListener('resize', this.handleResize);
    // 清空动态生成的DOM
    if (this.klineChart) {
      this.klineChart.innerHTML = '';
    }
  },
  methods: {
    // 初始化K线数据
    initKlines() {
      this.klines = [];
      this.klineElements = [];
      let currentPrice = this.CONFIG.basePrice;
      const now = Date.now();

      // 生成初始K线数据
      for (let i = 0; i < this.CONFIG.klineCount; i++) {
        const kline = this.generateKline(
          currentPrice,
          now - (this.CONFIG.klineCount - i) * this.CONFIG.updateInterval
        );
        this.klines.push(kline);
        currentPrice = kline.close;
        // 预创建K线DOM元素
        this.klineElements.push(this.createEmptyKlineElements());
      }

      // 初始化网格线
      this.createGridLines();
      this.renderChart();
    },
    // 预创建空的K线+影线元素
    createEmptyKlineElements() {
      const candle = document.createElement('div');
      candle.className = 'kline-candle';
      this.klineChart.appendChild(candle);

      const wick = document.createElement('div');
      wick.className = 'kline-wick';
      this.klineChart.appendChild(wick);

      return { candle, wick };
    },
    // 生成单根K线数据
    generateKline(prevPrice, timestamp) {
      const volatility = this.CONFIG.volatility / 100;
      const inertiaFactor = 0.1;
      const baseChange = (Math.random() - 0.5) * 2 * volatility;
      const change = baseChange * (1 - inertiaFactor) + (Math.random() - 0.5) * inertiaFactor;

      const open = prevPrice;
      const close = open * (1 + change);

      const high = Math.max(open, close) * (1 + Math.random() * volatility / 4);
      const low = Math.min(open, close) * (1 - Math.random() * volatility / 4);

      const type = close >= open ? 'up' : 'down';

      return {
        timestamp,
        open,
        high,
        low,
        close,
        type
      };
    },
    // 渲染K线图
    renderChart() {
      const containerWidth = this.klineChart.clientWidth;
      const containerHeight = this.klineChart.clientHeight;

      // 更新价格极值缓存
      this.updatePriceExtremesCache();
      const { minPrice, maxPrice } = this.priceExtremesCache;

      // 更新价格标签
      this.updatePriceLabels(minPrice, maxPrice, containerHeight);

      // 计算K线布局参数
      const klineSpacing = containerWidth / this.CONFIG.klineCount;
      const klineWidth = Math.min(12, klineSpacing * 0.7);

      // 渲染每根K线
      this.klines.forEach((kline, index) => {
        if (!this.klineElements[index]) return;

        const { candle, wick } = this.klineElements[index];
        const x = index * klineSpacing + (klineSpacing - klineWidth) / 2;

        // 计算坐标
        const openY = this.getPriceY(kline.open, minPrice, maxPrice, containerHeight);
        const closeY = this.getPriceY(kline.close, minPrice, maxPrice, containerHeight);
        const highY = this.getPriceY(kline.high, minPrice, maxPrice, containerHeight);
        const lowY = this.getPriceY(kline.low, minPrice, maxPrice, containerHeight);

        // 更新K线实体
        candle.className = `kline-candle ${kline.type}-candle`;
        candle.style.left = `${x}px`;
        candle.style.width = `${klineWidth}px`;
        candle.style.height = `${Math.max(1, Math.abs(closeY - openY))}px`;
        candle.style.top = `${Math.min(openY, closeY)}px`;

        // 更新影线
        wick.className = `kline-wick ${kline.type}-wick`;
        wick.style.left = `${x + klineWidth / 2 - 0.5}px`;
        wick.style.height = `${Math.max(1, lowY - highY)}px`;
        wick.style.top = `${highY}px`;
      });
    },
    // 创建水平网格线【修改点1：避免gridLines=0时除以0】
    createGridLines() {
      // 清空旧网格线
      const oldGridLines = this.klineChart.querySelectorAll('.grid-line');
      oldGridLines.forEach(el => el.remove());

      // 当gridLines≤0时，直接返回，不创建任何网格线（避免除以0）
      if (this.CONFIG.gridLines <= 0) return;

      // 仅创建水平网格线
      const containerHeight = this.klineChart.clientHeight;
      for (let i = 0; i <= this.CONFIG.gridLines; i++) {
        const line = document.createElement('div');
        line.className = 'grid-line horizontal';
        line.style.top = `${(i / this.CONFIG.gridLines) * (containerHeight - 40) + 20}px`;
        this.klineChart.appendChild(line);
      }
    },
    // 更新价格标签【修改点2：避免gridLines=0时除以0，移除NaN标签】
    updatePriceLabels(minPrice, maxPrice, containerHeight) {
      // 当gridLines≤0时，移除所有价格标签并返回（避免除以0生成NaN）
      if (this.CONFIG.gridLines <= 0) {
        const allLabels = this.klineChart.querySelectorAll('.price-label');
        allLabels.forEach(el => el.remove());
        return;
      }

      // 获取现有标签
      let labels = this.klineChart.querySelectorAll('.price-label');
      const needCreate = this.CONFIG.gridLines + 1 - labels.length;

      // 补充创建缺失的标签
      for (let i = 0; i < needCreate; i++) {
        const label = document.createElement('div');
        label.className = 'price-label';
        this.klineChart.appendChild(label);
      }

      // 更新标签文本和位置
      labels = this.klineChart.querySelectorAll('.price-label');
      labels.forEach((label, i) => {
        if (i > this.CONFIG.gridLines) {
          label.remove();
          return;
        }
        const price = maxPrice - (i / this.CONFIG.gridLines) * (maxPrice - minPrice);
        // 额外防护：确保价格不是NaN（兜底）
        label.textContent = isNaN(price) ? '' : price.toFixed(this.CONFIG.pricePrecision);
        label.style.top = `${(i / this.CONFIG.gridLines) * (containerHeight - 40) + 10}px`;
      });
    },
    // 获取价格对应的Y坐标
    getPriceY(price, minPrice, maxPrice, containerHeight) {
      const ratio = (price - minPrice) / (maxPrice - minPrice);
      return containerHeight - (ratio * (containerHeight - 40)) - 20;
    },
    // 更新价格极值缓存【兜底防护：避免min/max为极值导致NaN】
    updatePriceExtremesCache() {
      let min = Number.MAX_VALUE;
      let max = Number.MIN_VALUE;

      this.klines.forEach(kline => {
        min = Math.min(min, kline.low);
        max = Math.max(max, kline.high);
      });

      // 兜底：如果min/max是极值，替换为基础价格（避免NaN）
      min = isNaN(min) ? this.CONFIG.basePrice : min;
      max = isNaN(max) ? this.CONFIG.basePrice : max;

      this.priceExtremesCache = {
        minPrice: min,
        maxPrice: max,
        range: max - min || 1 // 避免除以0
      };
    },
    // 更新K线数据
    updateKlines() {
      // 生成新K线
      const lastKline = this.klines[this.klines.length - 1];
      const newKline = this.generateKline(lastKline.close, Date.now());

      // 添加新K线
      this.klines.push(newKline);

      // 移除最旧的K线
      if (this.klines.length > this.CONFIG.klineCount) {
        this.klines.shift();
        // 复用DOM元素
        const oldestElements = this.klineElements.shift();
        this.klineElements.push(oldestElements);
      } else {
        this.klineElements.push(this.createEmptyKlineElements());
      }

      // 重新渲染
      this.renderChart();
    },
    // 动画循环
    animate(timestamp) {
      if (!this.lastUpdateTime) {
        this.lastUpdateTime = timestamp;
      }

      // 计算更新间隔
      const speedFactor = Math.max(1, Math.min(10, this.CONFIG.speed));
      const actualInterval = 1000 - (speedFactor - 1) * 100;

      // 达到间隔更新K线
      if (timestamp - this.lastUpdateTime >= actualInterval) {
        this.updateKlines();
        this.lastUpdateTime = timestamp;
      }

      this.animationFrameId = requestAnimationFrame(this.animate);
    },
    // 处理窗口大小变化
    handleResize() {
      this.createGridLines();
      this.renderChart();
    }
  }
};
</script>

<style lang="css">
.kline-chart-wrapper {
  width: 100%;
  height: 800px;
  background-color: #181A20;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  opacity: 0.5;
}

.chart-container {
  position: relative;
  width: 100%;
  max-width: 1500px;
  height: 600px;
  border-radius: 8px;
  overflow: hidden;
  background-color: #181A20;
  /* box-shadow: 0 10px 30px rgba(0, 0, 0, 0.7); */
  /* border: 1px solid rgba(52, 152, 219, 0.15); */
}

#kline-chart {
  width: 100%;
  height: 100%;
  position: relative;
}

/* 仅保留水平网格线 */
.grid-line {
  position: absolute;
  background-color: rgba(255, 255, 255, 0.05);
}

.horizontal {
  width: 100%;
  height: 1px;
  left: 0;
}

/* 价格标签 */
.price-label {
  position: absolute;
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  font-family: monospace;
  right: 5px;
}

/* K线样式 - 过渡动画 */
.kline-candle {
  position: absolute;
  border-radius: 1px;
  z-index: 2;
  transition: top 0.2s ease, height 0.2s ease, background-color 0.1s ease;
}

.kline-wick {
  position: absolute;
  width: 1px;
  z-index: 1;
  transition: top 0.2s ease, height 0.2s ease, background-color 0.1s ease;
}

/* 上涨K线 */
.up-candle {
  background-color: rgba(46, 204, 113, 0.8);
  box-shadow: 0 0 10px rgba(46, 204, 113, 0.3);
}

/* 下跌K线 */
.down-candle {
  background-color: rgba(231, 76, 60, 0.8);
  box-shadow: 0 0 10px rgba(231, 76, 60, 0.3);
}

/* 上涨影线 */
.up-wick {
  background-color: rgba(46, 204, 113, 0.9);
}

/* 下跌影线 */
.down-wick {
  background-color: rgba(231, 76, 60, 0.9);
}
</style>